// «Отчётность → Пропуски АИС»: список MMSI → где каждое судно молчало дольше
// порога и были ли в эфире суда рядом. Отчёт считается заданием на сервере
// (минуты: треки по судну и соседям), страница следит за ним и рисует итог —
// Гант, карту и таблицу пропусков. Отчёт строится по всем источникам сразу —
// группой заданий, и все источники стоят на ОДНОЙ диаграмме: цвет и уровень
// бусинки — номер источника («Источник 1…3», имени кабинет не знает). Таблица и
// карта — общие пропуски (молчали все источники; задание с номером 0). Клик по
// отчёту в «Моих отчётах» переносит его настройки в форму. Открытый отчёт лежит
// в адресе (`?job=<отчёт>`); прежний `&src=` не читается.

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, cabinetApi } from '../api'
import type { AisGapGroup, AisGapReport, AisGapResult } from '../api'
import { errorText } from '../i18n/apiLabels'
import { formatDayTime, formatNumber } from '../i18n/format'
import { useLang, useT } from '../i18n'
import { addDays, boundIso, defaultFrom, formFromGroup, groupProgress, groupStatus, latestUntil, previewMmsi, progressLabel,
  reportProgress, SOURCE_NOS, sourceTab, splitRuns, windowProblem } from './aisGapsForm'
import type { GroupStatus } from './aisGapsForm'
import { GapsGantt } from './GapsGantt'
import { GapsMap } from './GapsMap'
import { vesselColors, type SourceLane } from './ganttLayout'

const HOURS = Array.from({ length: 24 }, (_, h) => h)
const open = (j: { status: string }) => j.status === 'queued' || j.status === 'running'
const iso = (t: number) => new Date(t * 1000).toISOString()

const STATUS_TONE: Record<GroupStatus, string> = {
  queued: 'bg-deep text-mist',
  partial: 'bg-amber-500/15 text-amber-300',
  running: 'bg-sonar/20 text-foam',
  done: 'bg-emerald-500/15 text-emerald-300',
  failed: 'bg-red-500/15 text-red-300',
}

function HourSelect({ value, onChange, label }: { value: number; onChange: (h: number) => void; label: string }) {
  return (
    <select aria-label={label} value={value} onChange={e => onChange(Number(e.target.value))}
      className="tnum rounded-lg border border-hull bg-deep px-2 py-1.5 text-[13px] text-foam">
      {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
    </select>
  )
}

export function AisGapsReport() {
  const t = useT()
  const { lang } = useLang()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [from, setFrom] = useState(() => defaultFrom(new Date()))
  const [fromHour, setFromHour] = useState(0)
  const [to, setTo] = useState('')
  const [toHour, setToHour] = useState(0)
  const [threshold, setThreshold] = useState('6')
  const [error, setError] = useState<string | null>(null)
  // Отчёт, выбранный кликом: его настройки уедут в форму, когда приедет детальный ответ.
  const [copyFrom, setCopyFrom] = useState<number | null>(null)

  const list = useQuery({
    queryKey: ['reports', 'ais-gaps'],
    queryFn: () => cabinetApi.aisGapReports(),
    refetchInterval: q => (q.state.data?.groups.some(g => g.sources.some(open)) ? 3000 : false),
    retry: false,
  })
  const groups = list.data?.groups ?? []
  const groupId = Number(params.get('job')) || groups[0]?.id || null
  const group = groups.find(g => g.id === groupId) ?? null
  // Отчёта нет в списке (старая ссылка) — номер в адресе читается как задание.
  const ids = group ? group.sources.map(x => x.id) : groupId != null ? [groupId] : []
  const reports = useQueries({
    queries: ids.map(id => ({
      queryKey: ['reports', 'ais-gaps', id],
      queryFn: () => cabinetApi.aisGapReport(id),
      refetchInterval: (q: { state: { data?: AisGapReport } }) =>
        (q.state.data && open(q.state.data) ? 3000 : false),
      retry: false,
    })),
  })
  const loaded = reports.map(r => r.data).filter((r): r is AisGapReport => !!r)
  const reportError = reports.find(r => r.error)?.error ?? null

  const preview = previewMmsi(text)
  const max = list.data?.max_vessels ?? 50
  const tooMany = preview.mmsis.length > max
  const tFrom = boundIso(from, fromHour)
  const tTo = to ? boundIso(to, toHour) : null
  const until = latestUntil(list.data)
  const problem = windowProblem(tFrom, tTo, until, list.data?.max_days ?? 31)
  const busy = groups.some(g => g.sources.some(open))
  const off = list.data?.unavailable ?? []

  // Автовыбор первого отчёта и ссылка `?job=` форму не трогают — их выбрал не человек.
  const first = loaded[0]
  useEffect(() => {
    const r = first
    if (copyFrom == null || !r || group?.id !== copyFrom || !group.sources.some(s => s.id === r.id)) return
    const f = formFromGroup(group, r.mmsis)
    setText(f.text); setTitle(f.title)
    setFrom(f.from); setFromHour(f.fromHour); setTo(f.to); setToHour(f.toHour)
    setThreshold(f.threshold)
    setCopyFrom(null)
  }, [copyFrom, first, group])

  const create = useMutation({
    mutationFn: () => cabinetApi.createAisGapReport({
      text, t_from: tFrom!, t_to: tTo, threshold_h: Number(threshold), title: title.trim() || null,
    }),
    onSuccess: r => {
      setError(null)
      setParams(p => {
        const n = new URLSearchParams(p); n.set('job', String(r.group)); n.delete('src'); return n
      }, { replace: true })
      qc.invalidateQueries({ queryKey: ['reports', 'ais-gaps'] })
    },
    onError: e => setError(e instanceof ApiError ? errorText(e.detail) : String(e)),
  })
  const blocked = busy || off.length === SOURCE_NOS.length || create.isPending || !preview.mmsis.length || tooMany || problem !== null
  const preset = (days: number) => { if (from) { setTo(addDays(from, days)); setToHour(fromHour) } }
  const [confirming, setConfirming] = useState<number | null>(null)
  const remove = useMutation({
    mutationFn: (id: number) => cabinetApi.deleteAisGapReport(id),
    onSuccess: (_, id) => {
      setError(null)
      setConfirming(null)
      if (Number(params.get('job')) === id) {
        setParams(p => { const n = new URLSearchParams(p); n.delete('job'); n.delete('src'); return n },
          { replace: true })
      }
      for (const s of groups.find(g => g.id === id)?.sources ?? []) {
        qc.removeQueries({ queryKey: ['reports', 'ais-gaps', s.id] })
      }
      qc.invalidateQueries({ queryKey: ['reports', 'ais-gaps'] })
    },
    onError: e => { setConfirming(null); setError(e instanceof ApiError ? errorText(e.detail) : String(e)) },
  })
  const pick = (id: number) => {
    setCopyFrom(id)
    setParams(p => { const n = new URLSearchParams(p); n.set('job', String(id)); n.delete('src'); return n },
      { replace: true })
  }

  if (list.error) {
    return <p className="text-sm text-mist">{list.error instanceof ApiError ? errorText(list.error.detail) : t('reports.result.error')}</p>
  }

  const field = 'rounded-lg border border-hull bg-deep px-2 py-1.5 text-[13px] text-foam'
  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-sm text-mist">{t('reports.aisGaps.lead')}</p>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <form className="grid gap-4 rounded-xl border border-hull bg-abyss/60 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]"
          onSubmit={e => { e.preventDefault(); if (!blocked) create.mutate() }}>
          <div className="flex flex-col">
            <label htmlFor="rg-mmsi" className="text-sm font-medium text-foam">{t('reports.form.vessels')}</label>
            <span className="text-[12px] text-mist">{t('reports.form.vesselsHint')}</span>
            <textarea id="rg-mmsi" value={text} onChange={e => setText(e.target.value)} spellCheck={false}
              placeholder={'273444010\n273437790'}
              className={`${field} mt-2 min-h-[11rem] flex-1 resize-y font-mono leading-6`} />
            <div className="tnum mt-1.5 text-[12px]" aria-live="polite">
              <span className={tooMany ? 'font-medium text-red-300' : 'text-mist'}>
                {t('reports.form.count', { count: preview.mmsis.length, max })}
              </span>
              {preview.rejected.length > 0 && (
                <div className="mt-1 text-amber-300">{t('reports.form.rejected', { list: preview.rejected.join(', ') })}</div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <fieldset>
              <legend className="text-sm font-medium text-foam">{t('reports.form.window')}</legend>
              <div className="mt-2 grid gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="w-6 text-[12px] text-mist">{t('reports.form.from')}</span>
                  <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={field} />
                  <HourSelect value={fromHour} onChange={setFromHour} label={t('reports.form.fromHour')} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="w-6 text-[12px] text-mist">{t('reports.form.to').split(',')[0]}</span>
                  <input type="date" value={to} onChange={e => setTo(e.target.value)} className={field}
                    aria-label={t('reports.form.to')} />
                  <HourSelect value={toHour} onChange={setToHour} label={t('reports.form.toHour')} />
                </div>
                <span className="text-[12px] text-mist">{t('reports.form.to')}</span>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => preset(1)} disabled={!from}
                    className="rounded-lg border border-hull px-2.5 py-1 text-[12px] text-mist hover:text-foam">
                    {t('reports.form.day')}
                  </button>
                  <button type="button" onClick={() => preset(7)} disabled={!from}
                    className="rounded-lg border border-hull px-2.5 py-1 text-[12px] text-mist hover:text-foam">
                    {t('reports.form.week')}
                  </button>
                </div>
              </div>
              {problem && from && (
                <p className="mt-1.5 text-[12px] text-amber-300">
                  {t(`reports.form.problem.${problem}`, {
                    max: list.data?.max_days ?? 31,
                    until: formatDayTime(lang, until),
                  })}
                </p>
              )}
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <label className="block">
                <span className="text-[12px] text-mist">{t('reports.form.threshold')}</span>
                <input type="number" min="1" step="1" value={threshold} onChange={e => setThreshold(e.target.value)}
                  className={`${field} tnum mt-1 block w-20`} />
              </label>
              <label className="block min-w-0 flex-1">
                <span className="text-[12px] text-mist">{t('reports.form.name')}</span>
                <input value={title} onChange={e => setTitle(e.target.value)} maxLength={120}
                  placeholder={t('reports.form.namePlaceholder')} className={`${field} mt-1 block w-full`} />
              </label>
            </div>
            <button type="submit" disabled={blocked}
              className="rounded-lg bg-sonar px-4 py-2 text-sm font-medium text-abyss disabled:opacity-40">
              {create.isPending ? t('reports.form.submitting') : t('reports.form.submit')}
            </button>
            {busy && <p className="text-[12px] text-mist">{t('errors.report_running')}</p>}
            {!busy && off.length > 0 && <p className="text-[12px] text-mist">{t('reports.source.unavailable')}</p>}
            {error && <p role="alert" className="text-[12px] text-red-300">{error}</p>}
          </div>
        </form>

        <section className="rounded-xl border border-hull bg-abyss/60 p-4">
          <h2 className="mb-2 text-sm font-medium text-foam">{t('reports.list.title')}</h2>
          {!groups.length && <p className="text-[13px] text-mist">{t('reports.list.empty')}</p>}
          <ul className="max-h-[22rem] space-y-1 overflow-y-auto">
            {groups.map(g => {
              const status = groupStatus(g)
              const gaps = g.sources.filter(s => s.gaps != null)
              return (
              <li key={g.id} className={`group flex items-start rounded-lg hover:bg-deep ${g.id === groupId ? 'bg-deep' : ''}`}>
                <button type="button" onClick={() => pick(g.id)}
                  className="min-w-0 flex-1 px-2.5 py-2 text-left">
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-foam">{g.title || t('reports.list.untitled')}</span>
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] ${STATUS_TONE[status]}`}>
                      {t(`reports.status.${status}`)}
                    </span>
                  </div>
                  <div className="tnum mt-0.5 text-[11px] text-mist">
                    {t('reports.list.meta', {
                      vessels: g.vessels, from: formatDayTime(lang, g.t0), to: formatDayTime(lang, g.t1),
                      threshold: formatNumber(lang, g.threshold_h),
                    })}
                    {gaps.length > 0 && `; ${t('reports.list.gaps', {
                      gaps: gaps.map(s => `${t(`reports.source.${s.source}`)} — ${s.gaps}`).join(', '),
                    })}`}
                  </div>
                </button>
                {status !== 'queued' && status !== 'running' && (confirming === g.id ? (
                  <span className="flex shrink-0 items-center gap-2 px-2 py-2 text-[12px]">
                    <button type="button" onClick={() => remove.mutate(g.id)} disabled={remove.isPending}
                      className="text-coral hover:underline disabled:opacity-50">{t('reports.list.deleteConfirm')}</button>
                    <button type="button" onClick={() => setConfirming(null)}
                      className="text-mist hover:underline">{t('reports.list.deleteCancel')}</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirming(g.id)}
                    aria-label={t('reports.list.delete')} title={t('reports.list.delete')}
                    className="shrink-0 px-2 py-2 text-[15px] leading-none text-mist opacity-60 hover:text-coral hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100">
                    ×
                  </button>
                ))}
              </li>
              )
            })}
          </ul>
        </section>
      </div>

      {reportError ? (
        <p className="text-sm text-mist">
          {reportError instanceof ApiError ? errorText(reportError.detail) : t('reports.result.error')}
        </p>
      ) : group ? <GroupBody group={group} reports={loaded} list={list.data} />
        : loaded[0] ? <ReportBody report={loaded[0]} /> : null}
    </div>
  )
}

/**
 * Отчёт целиком: пока идёт хоть одно задание — общая полоса; затем одна
 * диаграмма по всем источникам, а карта и таблица — общих пропусков.
 */
function GroupBody({ group, reports, list }: {
  group: AisGapGroup; reports: AisGapReport[]; list: Parameters<typeof sourceTab>[2]
}) {
  const t = useT()
  const { lang } = useLang()
  const { runs, combined } = splitRuns(group)
  const byId = new Map(reports.map(r => [r.id, r]))
  const runReports = runs.map(r => byId.get(r.id)).filter((r): r is AisGapReport => !!r)
  const combinedReport = combined ? byId.get(combined.id) ?? null : null
  const all = combinedReport ? [...runReports, combinedReport] : runReports
  const busy = group.sources.some(open)
  if (busy || all.length < group.sources.length) {
    const fraction = groupProgress(all, !combined)
    return (
      <section className="rounded-xl border border-hull bg-abyss/60 p-4" aria-live="polite">
        <div className="tnum text-sm text-foam">
          {t('reports.progress.group', {
            done: group.sources.filter(x => !open(x)).length, total: group.sources.length + (combined ? 0 : 1),
          })}
        </div>
        {all.filter(open).map(r => {
          const l = progressLabel(reportProgress(r))
          return (
            <div key={r.id} className="tnum mt-1 text-[12px] text-mist">
              {t(`reports.source.${r.source}`)}: {t(l.key, l.params)}
            </div>
          )
        })}
        <div className="mt-2 h-1.5 w-full rounded-full bg-deep">
          <div className="h-1.5 rounded-full bg-sonar transition-[width] duration-500"
            style={{ width: `${100 * fraction}%` }} />
        </div>
      </section>
    )
  }
  const lanes: SourceLane[] = runReports
    .filter(r => r.status === 'done' && r.result)
    .map(r => ({
      no: r.source, window: r.result!.window, result: r.result!,
      // соседи общего пропуска — уровнем источника, чья это сеть
      more: combinedReport?.status === 'done' && combinedReport.result
        && combinedReport.neighbors_from === r.source ? [combinedReport.result] : undefined,
    }))
  const base = combinedReport?.status === 'done' && combinedReport.result ? combinedReport.result
    : lanes[0]?.result ?? null
  const notes = runs.map(r => {
    const st = sourceTab(group, r.source as 1 | 2 | 3, list)
    const name = t(`reports.source.${r.source}`)
    if (r.status === 'failed') return `${name}: ${t('reports.status.failed')}`
    return st.kind === 'run' && st.cutAt
      ? `${name}: ${t('reports.tab.until', { until: formatDayTime(lang, st.cutAt) })}` : null
  }).filter((x): x is string => !!x)
  for (const n of SOURCE_NOS) {
    if (runs.some(r => r.source === n)) continue
    const st = sourceTab(group, n, list)
    notes.push(`${t(`reports.source.${n}`)}: ${t(st.kind === 'no_data' ? 'reports.tab.noData' : 'reports.tab.off')}`)
  }
  if (!base) {
    return <p className="text-sm text-red-300">{t('reports.status.failed')}</p>
  }
  return (
    <div className="space-y-3">
      {notes.length > 0 && <p className="text-[12px] text-mist">{notes.join(' · ')}</p>}
      {combinedReport?.status === 'failed' && (
        <p className="text-[12px] text-red-300">{t('reports.result.combinedFailed')}</p>
      )}
      <ReportResult result={base} sources={lanes.length > 1 || combinedReport ? lanes : []} />
    </div>
  )
}

function ReportBody({ report }: { report: AisGapReport }) {
  const t = useT()
  if (open(report)) {
    const p = reportProgress(report)
    return (
      <section className="rounded-xl border border-hull bg-abyss/60 p-4" aria-live="polite">
        <div className="tnum text-sm text-foam">
          {(l => t(l.key, l.params))(progressLabel(p))}
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-deep">
          <div className="h-1.5 rounded-full bg-sonar transition-[width] duration-500"
            style={{ width: `${100 * p.fraction}%` }} />
        </div>
      </section>
    )
  }
  if (report.status === 'failed') {
    return <p className="text-sm text-red-300">{t('reports.status.failed')}: {report.error}</p>
  }
  return report.result ? <ReportResult result={report.result} /> : null
}

function ReportResult({ result, sources }: { result: AisGapResult; sources?: SourceLane[] }) {
  const t = useT()
  const { lang } = useLang()
  const colors = useMemo(() => vesselColors(result), [result])
  const [focus, setFocus] = useState<{ no: number; nonce: number } | null>(null)
  const toGap = (no: number) => setFocus(f => ({ no, nonce: (f?.nonce ?? 0) + 1 }))
  const names = new Map(result.vessels.map(v => [v.mmsi, v.name]))
  return (
    <div className="space-y-6">
      <p className="tnum text-sm text-mist">
        {t('reports.result.summary', {
          vessels: result.vessels.length, threshold: formatNumber(lang, result.threshold_h),
          gaps: result.gaps.length, km: result.radius_km,
        })}
      </p>
      <section>
        <h2 className="mb-2 text-sm font-medium text-foam">{t('reports.gantt.title')}</h2>
        <div className="rounded-xl border border-hull bg-abyss/60 p-3">
          <GapsGantt result={result} sources={sources} onGap={toGap} />
        </div>
      </section>
      <section>
        <h2 className="mb-2 text-sm font-medium text-foam">{t('reports.map.title')}</h2>
        <GapsMap result={result} colors={colors} focus={focus} />
      </section>
      <section>
        <h2 className="mb-2 text-sm font-medium text-foam">{t('reports.table.title')}</h2>
        {!result.gaps.length ? <p className="text-sm text-mist">{t('reports.table.empty')}</p> : (
          <div className="overflow-x-auto rounded-xl border border-hull">
            <table className="tnum w-full text-left text-[13px]">
              <thead className="bg-deep text-[12px] text-mist">
                <tr>
                  {(['no', 'vessel', 'start', 'end', 'hours', 'place', 'neighbors', 'verdict'] as const).map(k => (
                    <th key={k} className="px-3 py-2 font-medium">{t(`reports.table.${k}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.gaps.map(g => {
                  const name = names.get(g.mmsi)
                  return (
                    <tr key={g.no} onClick={() => toGap(g.no)}
                      className="cursor-pointer border-t border-hull text-foam hover:bg-deep">
                      <td className="px-3 py-1.5 font-semibold" style={{ color: g.port ? undefined : '#ef4444' }}>{g.no}</td>
                      <td className="px-3 py-1.5">
                        <span className="mr-1.5 inline-block h-2 w-2 rounded-sm" style={{ background: colors.get(g.mmsi) }} />
                        {name ? `${name} (${g.mmsi})` : g.mmsi}
                      </td>
                      <td className="px-3 py-1.5">{formatDayTime(lang, iso(g.start))}</td>
                      <td className="px-3 py-1.5">{formatDayTime(lang, iso(g.end))}</td>
                      <td className="px-3 py-1.5">{formatNumber(lang, g.hours, 1)}</td>
                      <td className="px-3 py-1.5 text-mist">
                        {g.port ? t('reports.table.atPort', { port: g.port.name, km: formatNumber(lang, g.port.km, 1) })
                          : t('reports.table.atSea')}
                        {g.edge && `; ${t('reports.table.edge')}`}
                      </td>
                      <td className="px-3 py-1.5">
                        {g.check && g.check.verdict !== 'unchecked'
                          ? t('reports.table.neighborsCount', { visible: g.check.visible, checked: g.check.checked })
                          : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-mist">
                        {g.check ? t(`reports.verdict.${g.check.verdict}`) : ''}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
